// Open a new window with ONLY the document content for printing.
// Avoids CSS conflicts with the main app's Radix Dialog.

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const nl2br = (s) => escapeHtml(s).replace(/\n/g, "<br>");

export function printDocOnly(doc, content, schema, title, patient, org) {
  const headerDate =
    content.issue_date ||
    content.plan_date ||
    content.contract_date ||
    content.application_date ||
    content.meeting_date ||
    content.report_date ||
    content.date ||
    "";

  const patientHtml = patient
    ? `
    <section>
      <h2>利用者情報</h2>
      <table>
        <tbody>
          <tr><td class="lbl">氏名</td><td>${escapeHtml(patient.name_kanji || "")}${patient.name_kana ? `(${escapeHtml(patient.name_kana)})` : ""}</td></tr>
          <tr><td class="lbl">生年月日</td><td>${escapeHtml(patient.birth_date || "")}</td></tr>
          <tr><td class="lbl">住所</td><td>${escapeHtml(patient.address || "")}</td></tr>
          <tr><td class="lbl">電話</td><td>${escapeHtml(patient.phone || "")}</td></tr>
          <tr><td class="lbl">主治医</td><td>${escapeHtml(patient.primary_doctor_name || "")}${patient.primary_medical_institution ? ` / ${escapeHtml(patient.primary_medical_institution)}` : ""}</td></tr>
        </tbody>
      </table>
    </section>`
    : "";

  const sectionsHtml = (schema?.sections || [])
    .map((sec) => {
      const rows = (sec.fields || [])
        .filter((f) => {
          if (f.type === "signature") return false;
          const v = content[f.key];
          return v !== undefined && v !== "" && v !== null && v !== false;
        })
        .map((f) => {
          const v = content[f.key];
          const text =
            f.type === "check" ? (v ? "☑ 同意" : "") : nl2br(String(v));
          return `<tr><td class="lbl">${escapeHtml(f.label)}</td><td>${text}</td></tr>`;
        })
        .join("");
      if (!rows) return "";
      return `<section><h2>${escapeHtml(sec.title)}</h2><table><tbody>${rows}</tbody></table></section>`;
    })
    .join("");

  const sigHtml = `
    <section class="signatures">
      <div class="sig-grid">
        <div>
          <div class="sig-title">事業所</div>
          <div>${escapeHtml(org?.name || "")}</div>
          <div>${escapeHtml(org?.address || "")}</div>
          <div>TEL: ${escapeHtml(org?.phone || "")}</div>
          <div>管理者: ${escapeHtml(org?.manager_name || "")}</div>
          <div>指定番号: ${escapeHtml(org?.care_insurance_number || "")}</div>
          ${content.provider_signature ? `<div class="sig-img"><img src="${content.provider_signature}" alt="事業所署名"></div>` : ""}
          <div class="sig-line">
            氏名: ${escapeHtml(content.provider_signer || "")} <span class="hanko">印</span>
          </div>
        </div>
        <div>
          <div class="sig-title">利用者・ご家族</div>
          ${content.client_signature ? `<div class="sig-img"><img src="${content.client_signature}" alt="利用者署名"></div>` : ""}
          <div class="sig-line">
            氏名: ${escapeHtml(content.client_name || "")} <span class="hanko">印</span>
          </div>
          ${content.representative_name ? `<div>代理人: ${escapeHtml(content.representative_name)}(${escapeHtml(content.representative_relation || "")})</div>` : ""}
          ${content.signed_date ? `<div class="sig-date">署名日: ${escapeHtml(content.signed_date)}</div>` : ""}
        </div>
      </div>
    </section>`;

  const html = `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title || "書類")}</title>
<style>
  @page { size: A4 portrait; margin: 12mm 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; background: white; color: #111;
    font-family: "Shippori Mincho", "Yu Mincho", "MS Mincho", serif;
    font-size: 10pt; line-height: 1.55;
  }
  .doc {
    width: 100%;
  }
  .doc-header { text-align: center; margin-bottom: 6mm; }
  .doc-header h1 { font-size: 16pt; margin: 0 0 2mm 0; letter-spacing: 0.05em; }
  .doc-header .date { font-size: 9pt; color: #444; }
  section { margin-bottom: 4mm; page-break-inside: avoid; }
  h2 {
    font-size: 11pt; font-weight: bold; margin: 4mm 0 2mm 0;
    padding-bottom: 1mm; border-bottom: 0.4pt solid #000;
  }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 1mm 2mm; vertical-align: top; font-size: 9.5pt; }
  td.lbl { font-weight: bold; width: 32mm; }
  tr { page-break-inside: avoid; }
  .signatures { margin-top: 8mm; padding-top: 3mm; border-top: 0.6pt solid #000; }
  .sig-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 8mm;
  }
  .sig-title { font-weight: bold; margin-bottom: 1mm; }
  .sig-img img { height: 14mm; max-width: 60mm; object-fit: contain; }
  .sig-line { margin-top: 3mm; border-bottom: 0.4pt solid #000; padding-bottom: 1mm; }
  .hanko { color: #b22; font-size: 9pt; margin-left: 2mm; }
  .sig-date { font-size: 8.5pt; color: #444; margin-top: 1mm; }
</style>
</head>
<body>
  <div class="doc">
    <div class="doc-header">
      <h1>${escapeHtml(title || "")}</h1>
      ${headerDate ? `<div class="date">${escapeHtml(headerDate)}</div>` : ""}
    </div>
    ${patientHtml}
    ${sectionsHtml}
    ${sigHtml}
  </div>
  <script>
    window.addEventListener("load", function() {
      setTimeout(function() {
        window.focus();
        window.print();
      }, 250);
    });
    window.addEventListener("afterprint", function() {
      window.close();
    });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) {
    alert("ポップアップがブロックされています。ブラウザの設定で許可してください。");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// =====================================================================
// Print 訪問看護指示書依頼書 (主治医宛の依頼状レイアウト)
// =====================================================================
export function printInstructionRequest(content, title, org) {
  const c = content || {};
  const o = org || {};
  const cb = (v) => (v ? "☑" : "□");
  const postal = o.postal_code ? `〒${escapeHtml(o.postal_code)}　` : "";
  const bodyText = String(c.greeting_body || "").replace(/\s*敬白\s*$/, "").trim();

  const html = `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title || "訪問看護指示書依頼書")}</title>
<style>
  @page { size: A4 portrait; margin: 8mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; background: white; color: #111;
    font-family: "Yu Mincho", "MS Mincho", "Hiragino Mincho ProN", serif;
    font-size: 10.5pt; line-height: 1.75;
  }
  .frame {
    border: 1.2pt solid #000;
    padding: 7mm 8mm;
    min-height: 281mm;   /* A4(297) - 余白16 */
  }
  .head-right { text-align: right; margin-bottom: 4mm; }
  .indent { margin-left: 3em; }
  .indent2 { margin-left: 1.5em; }
  .block { margin-bottom: 3mm; }
  .lbl { display: inline-block; width: 6em; vertical-align: top; }
  .body { white-space: pre-wrap; margin-bottom: 1mm; }
  .right { text-align: right; }
  .center { text-align: center; margin: 1mm 0 2.5mm 0; }
  .mb { margin-bottom: 3mm; }
  .mb2 { margin-bottom: 7mm; }
  .sender div { line-height: 1.9; }
  .seal { height: 14mm; margin-top: 1mm; object-fit: contain; }

  /* A4 1枚に確実に収める */
  @media print {
    html, body { font-size: 10pt; line-height: 1.68; }
    .frame { min-height: 279mm; padding: 6mm 8mm; }
    .block { margin-bottom: 2.5mm; }
    .mb2 { margin-bottom: 6mm; }
  }
</style>
</head>
<body>
  <div class="frame">
  <div>${cb(c.request_type === "新規")} 新規　　　　${cb(c.request_type === "継続")} 継続</div>

  <div class="head-right">
    <div>依頼年月日　　${escapeHtml(c.request_date || "　　　年　　月　　日")}</div>
    <div>依頼受理担当者様　${escapeHtml(c.receiver_handler || "＿＿＿＿＿＿＿")}</div>
  </div>

  <div class="indent block">
    <div>訪問看護指示書依頼書</div>
    <div>${cb(!!c.req_self_support_cert)} 自立支援医療受給者証（精神通院医療）診断書依頼書</div>
    <div class="indent2">${cb(c.self_support_type === "新規")} 新規　　${cb(c.self_support_type === "更新")} 更新</div>
    <div>${cb(!!c.req_self_support_add)} 自立支援医療機関追加用意見書依頼書（訪問看護）</div>
  </div>

  <div><span class="lbl">医療機関名</span>${escapeHtml(c.medical_institution || "")}　${escapeHtml(c.department || "")}</div>
  <div class="mb"><span class="lbl">主治医名</span>${escapeHtml(c.doctor_name || "")}　${escapeHtml(c.doctor_honorific || "先生御侍史")}</div>

  <div class="body">${nl2br(bodyText)}</div>
  <div class="right">敬白</div>

  <div class="center">記</div>

  <div class="indent block">
    <div><span class="lbl">フリガナ</span>${escapeHtml(c.patient_kana || "")}</div>
    <div><span class="lbl">患者氏名</span>${escapeHtml(c.patient_name || "")}　　様</div>
    <div><span class="lbl">生年月日</span>${escapeHtml(c.patient_birth || "")}</div>
    <div><span class="lbl">性別</span>${escapeHtml(c.patient_gender || "")}</div>
    <div><span class="lbl">住所</span>${escapeHtml(c.patient_address || "")}</div>
    <div><span class="lbl">電話番号</span>${escapeHtml(c.patient_phone || "")}</div>
  </div>

  <div class="indent">
    <div>訪問看護指示書　利用目的：　${escapeHtml(c.purpose || "")}</div>
    <div style="padding-left:1em">・訪問看護指示開始日　${escapeHtml(c.instruction_start_date || "")}</div>
    <div style="padding-left:1em">・訪問内容　${escapeHtml(c.visit_content || "")}</div>
    <div>${cb(!!c.req_iv_instruction)} 在宅患者訪問点滴注射指示書</div>
    <div>${cb(!!c.req_special_instruction)} 特別訪問看護指示書</div>
    <div>${cb(!!c.req_self_support_cert)} 自立支援医療受給者証（精神通院医療）診断書依頼書</div>
    <div class="indent2">${cb(c.self_support_type === "新規")} 新規　　${cb(c.self_support_type === "更新")} 更新　期限${escapeHtml(c.self_support_limit || "　　　　")}（診断書 ${cb(c.self_support_cert_needed === "要")} 要 ${cb(c.self_support_cert_needed === "不要")} 不）</div>
    <div>${cb(!!c.req_self_support_add)} 自立支援医療機関追加用意見書　訪問看護ステーション</div>
  </div>
  <div class="right mb2">以上</div>

  <div class="sender right">
    <div>${escapeHtml(o.name || "")}</div>
    <div>${postal}${escapeHtml(o.address || "")}</div>
    <div>電話 ${escapeHtml(o.phone || "")}${o.fax ? `　FAX ${escapeHtml(o.fax)}` : ""}</div>
    ${(o.manager_name || o.emergency_phone) ? `<div>事務担当　${escapeHtml(o.manager_name || "")}${o.emergency_phone ? `　携帯${escapeHtml(o.emergency_phone)}` : ""}</div>` : ""}
    ${o.seal_base64 ? `<img class="seal" src="${o.seal_base64}" alt="印">` : ""}
  </div>
  </div>

  <script>
    window.addEventListener("load", function() {
      setTimeout(function() { window.focus(); window.print(); }, 250);
    });
    window.addEventListener("afterprint", function() { window.close(); });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) {
    alert("ポップアップがブロックされています。ブラウザの設定で許可してください。");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// =====================================================================
// Print patient basic information (氏名・主病名・主治医・家族・服薬等)
// =====================================================================
export function printPatientBasic(patient, org) {
  if (!patient) return;
  const today = new Date().toISOString().slice(0, 10);

  const row = (label, value) =>
    value
      ? `<tr><td class="lbl">${escapeHtml(label)}</td><td>${nl2br(value)}</td></tr>`
      : "";

  const familyHtml = (patient.family_members || []).length
    ? `<section><h2>家族情報</h2><table><thead><tr><th>続柄</th><th>氏名</th><th>電話</th></tr></thead><tbody>${(patient.family_members || [])
        .map(
          (f) =>
            `<tr><td>${escapeHtml(f.relation || "")}</td><td>${escapeHtml(f.name || "")}</td><td>${escapeHtml(f.phone || "")}</td></tr>`,
        )
        .join("")}</tbody></table></section>`
    : "";

  const emergencyHtml = (patient.emergency_contacts || []).length
    ? `<section><h2>緊急連絡先</h2><table><thead><tr><th>優先</th><th>氏名</th><th>続柄</th><th>電話</th></tr></thead><tbody>${(patient.emergency_contacts || [])
        .map(
          (e) =>
            `<tr><td>${escapeHtml(String(e.priority ?? ""))}</td><td>${escapeHtml(e.name || "")}</td><td>${escapeHtml(e.relation || "")}</td><td>${escapeHtml(e.phone || "")}</td></tr>`,
        )
        .join("")}</tbody></table></section>`
    : "";

  const html = `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="utf-8">
<title>利用者基本情報 ${escapeHtml(patient.name_kanji || "")}</title>
<style>
  @page { size: A4 portrait; margin: 12mm 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; background: white; color: #111;
    font-family: "Shippori Mincho", "Yu Mincho", "MS Mincho", serif;
    font-size: 10pt; line-height: 1.55;
  }
  .doc-header { text-align: center; margin-bottom: 6mm; }
  .doc-header h1 { font-size: 16pt; margin: 0 0 2mm 0; letter-spacing: 0.05em; }
  .doc-header .meta { font-size: 9pt; color: #444; }
  section { margin-bottom: 4mm; page-break-inside: avoid; }
  h2 {
    font-size: 11pt; font-weight: bold; margin: 4mm 0 2mm 0;
    padding-bottom: 1mm; border-bottom: 0.4pt solid #000;
  }
  table { width: 100%; border-collapse: collapse; }
  td, th { padding: 1mm 2mm; vertical-align: top; font-size: 9.5pt; }
  td.lbl, th { font-weight: bold; }
  td.lbl { width: 32mm; }
  th {
    background: #f5f5f3; border-bottom: 0.4pt solid #000;
    text-align: left; font-size: 9pt;
  }
  tr { page-break-inside: avoid; }
  .photo-block {
    float: right; width: 30mm; height: 38mm; border: 0.6pt solid #000;
    margin-left: 4mm; margin-bottom: 2mm;
    display: flex; align-items: center; justify-content: center;
    overflow: hidden;
  }
  .photo-block img { width: 100%; height: 100%; object-fit: cover; }
  .photo-block .ph-empty { font-size: 8pt; color: #888; }
  .footer {
    margin-top: 8mm; padding-top: 3mm; border-top: 0.4pt solid #000;
    display: flex; justify-content: space-between; font-size: 8.5pt; color: #555;
  }
</style>
</head>
<body>
  <div class="doc-header">
    <h1>利用者基本情報</h1>
    <div class="meta">${escapeHtml(org?.name || "")} ・ 発行日: ${today}</div>
  </div>

  <div class="photo-block">
    ${patient.photo_base64 ? `<img src="${patient.photo_base64}" alt="">` : '<span class="ph-empty">(顔写真)</span>'}
  </div>

  <section>
    <h2>個人情報</h2>
    <table><tbody>
      ${row("氏名(漢字)", patient.name_kanji)}
      ${row("氏名(カナ)", patient.name_kana)}
      ${row("生年月日", patient.birth_date)}
      ${row("性別", patient.gender)}
      ${row("郵便番号", patient.postal_code)}
      ${row("住所", patient.address)}
      ${row("電話番号", patient.phone)}
    </tbody></table>
  </section>

  <section>
    <h2>医療情報</h2>
    <table><tbody>
      ${row("主病名", patient.main_diagnosis)}
      ${row("要介護度", patient.care_level)}
      ${row("障害区分", patient.disability_grade)}
      ${row("主治医", patient.primary_doctor_name)}
      ${row("医療機関", patient.primary_medical_institution)}
      ${row("主治医 電話", patient.doctor_phone)}
    </tbody></table>
  </section>

  <section>
    <h2>状態・服薬等</h2>
    <table><tbody>
      ${row("ADL", patient.adl)}
      ${row("アレルギー", patient.allergies)}
      ${row("服薬情報", patient.medications)}
      ${row("既往歴", patient.medical_history)}
    </tbody></table>
  </section>

  ${familyHtml}
  ${emergencyHtml}

  <div class="footer">
    <span>${escapeHtml(org?.name || "")}</span>
    <span>TEL: ${escapeHtml(org?.phone || "")} / 管理者: ${escapeHtml(org?.manager_name || "")}</span>
  </div>

  <script>
    window.addEventListener("load", function() {
      setTimeout(function() {
        window.focus();
        window.print();
      }, 250);
    });
    window.addEventListener("afterprint", function() {
      window.close();
    });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) {
    alert("ポップアップがブロックされています。ブラウザの設定で許可してください。");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// =====================================================================
// Print nursing record (訪問看護記録書Ⅱ)
// =====================================================================
export function printNursingRecord(record, patient, org) {
  if (!record) return;
  const today = new Date().toISOString().slice(0, 10);

  const row = (label, value, unit) => {
    if (value === undefined || value === null || value === "") return "";
    return `<tr><td class="lbl">${escapeHtml(label)}</td><td>${nl2br(value)}${unit ? ` ${escapeHtml(unit)}` : ""}</td></tr>`;
  };

  const blockRow = (label, value) => {
    if (!value) return "";
    return `<tr><td class="lbl">${escapeHtml(label)}</td><td class="block">${nl2br(value)}</td></tr>`;
  };

  const vitalsHtml = [
    record.temperature ? `体温 ${record.temperature}℃` : "",
    record.bp_systolic || record.bp_diastolic ? `BP ${record.bp_systolic ?? "-"}/${record.bp_diastolic ?? "-"}` : "",
    record.pulse ? `脈拍 ${record.pulse}` : "",
    record.spo2 ? `SpO2 ${record.spo2}%` : "",
    record.respiration ? `呼吸 ${record.respiration}` : "",
  ].filter(Boolean).join(" ・ ");

  const soapHtml = (record.soap_s || record.soap_o || record.soap_a || record.soap_p)
    ? `
    <section>
      <h2>SOAP</h2>
      <table><tbody>
        ${blockRow("S (主観)", record.soap_s)}
        ${blockRow("O (客観)", record.soap_o)}
        ${blockRow("A (アセスメント)", record.soap_a)}
        ${blockRow("P (計画)", record.soap_p)}
      </tbody></table>
    </section>`
    : "";

  const html = `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="utf-8">
<title>訪問看護記録書Ⅱ ${escapeHtml(patient?.name_kanji || "")} ${escapeHtml(record.record_date || "")}</title>
<style>
  @page { size: A4 portrait; margin: 12mm 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; background: white; color: #111;
    font-family: "Shippori Mincho", "Yu Mincho", "MS Mincho", serif;
    font-size: 10pt; line-height: 1.55;
  }
  .doc-header { text-align: center; margin-bottom: 6mm; }
  .doc-header h1 { font-size: 16pt; margin: 0 0 2mm 0; letter-spacing: 0.05em; }
  .doc-header .meta { font-size: 9pt; color: #444; }
  section { margin-bottom: 4mm; page-break-inside: avoid; }
  h2 {
    font-size: 11pt; font-weight: bold; margin: 4mm 0 2mm 0;
    padding-bottom: 1mm; border-bottom: 0.4pt solid #000;
  }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 1mm 2mm; vertical-align: top; font-size: 9.5pt; }
  td.lbl { font-weight: bold; width: 34mm; background: #fafaf7; }
  td.block { white-space: pre-wrap; }
  tr { page-break-inside: avoid; border-bottom: 0.25pt solid #ddd; }
  .vitals-line { font-size: 10pt; padding: 1.5mm 2mm; border: 0.4pt solid #000; }
  .footer {
    margin-top: 8mm; padding-top: 3mm; border-top: 0.4pt solid #000;
    display: flex; justify-content: space-between; font-size: 8.5pt; color: #555;
  }
  .signature-area {
    margin-top: 10mm; display: flex; gap: 8mm; font-size: 9pt;
  }
  .signature-area .sig-box {
    flex: 1; border-bottom: 0.4pt solid #000; min-height: 14mm; padding: 1mm 2mm;
  }
  .signature-area .sig-label { font-size: 8pt; color: #555; }

  /* 印刷時のみ: A4 1枚に必ず収まるようコンパクト化 */
  @media print {
    @page { size: A4 portrait; margin: 10mm 12mm; }
    html, body { font-size: 8.5pt; line-height: 1.35; }
    .doc-header { margin-bottom: 3mm; }
    .doc-header h1 { font-size: 14pt; margin: 0 0 1mm 0; }
    .doc-header .meta { font-size: 8pt; }
    section { margin-bottom: 2mm; }
    h2 { font-size: 9pt; margin: 2mm 0 1mm 0; padding-bottom: 0.5mm; }
    td { padding: 0.6mm 1.5mm; font-size: 8pt; }
    td.lbl { width: 28mm; }
    .vitals-line { font-size: 8pt; padding: 0.8mm 1.5mm; }
    .signature-area { margin-top: 4mm; gap: 6mm; font-size: 8pt; }
    .signature-area .sig-box { min-height: 10mm; }
    .signature-area .sig-label { font-size: 7pt; }
    .footer { margin-top: 3mm; padding-top: 1.5mm; font-size: 7.5pt; }
  }
</style>
</head>
<body>
  <div class="doc-header">
    <h1>訪問看護記録書 Ⅱ</h1>
    <div class="meta">${escapeHtml(org?.name || "")} ・ 発行日: ${today}</div>
  </div>

  <section>
    <h2>基本情報</h2>
    <table><tbody>
      ${row("利用者氏名", patient?.name_kanji || "")}
      ${row("記録日", record.record_date)}
      ${row("看護師等氏名", record.nurse_name)}
      ${row("訪問職種", record.visit_profession)}
      ${row("訪問先", record.visit_location)}
      ${row("次回訪問予定日", record.next_visit_date)}
    </tbody></table>
  </section>

  ${vitalsHtml ? `
  <section>
    <h2>バイタルサイン</h2>
    <div class="vitals-line">${escapeHtml(vitalsHtml)}</div>
  </section>` : ""}

  ${(record.daily_living || record.mental_state || record.nursing_actions || record.gaf_score || (record.daily_living_details && Object.values(record.daily_living_details||{}).some(v=>v && String(v).trim()))) ? `
  <section>
    <h2>観察・看護内容</h2>
    <table><tbody>
      ${record.daily_living_details && Object.entries(record.daily_living_details||{}).filter(([,v])=>v && String(v).trim()).length > 0 ? `<tr><td class="lbl">日常生活</td><td>${Object.entries(record.daily_living_details).filter(([,v])=>v && String(v).trim()).map(([k,v]) => `<div><b>${escapeHtml(k)}:</b> ${nl2br(v)}</div>`).join("")}</td></tr>` : ""}
      ${blockRow("日常生活 補足", record.daily_living)}
      ${blockRow("精神状態 / 服薬 / 作業・対人関係", record.mental_state)}
      ${blockRow("実施した看護内容", record.nursing_actions)}
      ${row("GAFスコア", record.gaf_score)}
    </tbody></table>
  </section>` : ""}

  ${soapHtml}

  ${((record.observation_details && Object.values(record.observation_details || {}).some(v => v && String(v).trim())) || (record.bath_grooming_details && Object.values(record.bath_grooming_details || {}).some(v => v && String(v).trim())) || record.observation_checks?.length > 0 || record.bath_grooming_checks?.length > 0) ? `
  <section>
    <h2>観察・実施項目</h2>
    <table><tbody>
      ${record.observation_details && Object.entries(record.observation_details || {}).filter(([,v])=>v && String(v).trim()).length > 0 ? `<tr><td class="lbl">観察事項</td><td>${Object.entries(record.observation_details).filter(([,v])=>v && String(v).trim()).map(([k,v]) => `<div><b>${escapeHtml(k)}:</b> ${nl2br(v)}</div>`).join("")}</td></tr>` : (record.observation_checks?.length > 0 ? `<tr><td class="lbl">観察事項</td><td>${record.observation_checks.map(c => `<span style="display:inline-block;padding:0.5mm 1.5mm;margin:0.3mm 0.5mm 0.3mm 0;border:0.3pt solid #2C5545;border-radius:1mm;font-size:8.5pt;">☑ ${escapeHtml(c)}</span>`).join("")}</td></tr>` : "")}
      ${record.bath_grooming_details && Object.entries(record.bath_grooming_details || {}).filter(([,v])=>v && String(v).trim()).length > 0 ? `<tr><td class="lbl">入浴・整容</td><td>${Object.entries(record.bath_grooming_details).filter(([,v])=>v && String(v).trim()).map(([k,v]) => `<div><b>${escapeHtml(k)}:</b> ${nl2br(v)}</div>`).join("")}</td></tr>` : (record.bath_grooming_checks?.length > 0 ? `<tr><td class="lbl">入浴・整容</td><td>${record.bath_grooming_checks.map(c => `<span style="display:inline-block;padding:0.5mm 1.5mm;margin:0.3mm 0.5mm 0.3mm 0;border:0.3pt solid #4A6B8C;border-radius:1mm;font-size:8.5pt;">☑ ${escapeHtml(c)}</span>`).join("")}</td></tr>` : "")}
    </tbody></table>
  </section>` : ""}

  ${(record.procedures || record.observations || record.summary) ? `
  <section>
    <h2>処置・サマリー</h2>
    <table><tbody>
      ${blockRow("処置内容", record.procedures)}
      ${blockRow("観察事項 補足", record.observations)}
      ${blockRow("サマリー", record.summary)}
    </tbody></table>
  </section>` : ""}

  <div class="signature-area">
    <div>
      <div class="sig-label">記録者署名</div>
      <div class="sig-box">${escapeHtml(record.nurse_name || "")}</div>
    </div>
    <div>
      <div class="sig-label">管理者確認</div>
      <div class="sig-box"></div>
    </div>
  </div>

  <div class="footer">
    <span>${escapeHtml(org?.name || "")}</span>
    <span>TEL: ${escapeHtml(org?.phone || "")} / 管理者: ${escapeHtml(org?.manager_name || "")}</span>
  </div>

  <script>
    window.addEventListener("load", function() {
      setTimeout(function() {
        window.focus();
        window.print();
      }, 250);
    });
    window.addEventListener("afterprint", function() {
      window.close();
    });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) {
    alert("ポップアップがブロックされています。ブラウザの設定で許可してください。");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}


// =====================================================================
// Print MULTIPLE nursing records as a batch (formal: one record per page)
// `entries`: [{record, patient}, ...]
// =====================================================================
export function printNursingRecordsBatch(entries, org, filterLabel) {
  if (!entries || entries.length === 0) return;
  const today = new Date().toISOString().slice(0, 10);

  const row = (label, value, unit) => {
    if (value === undefined || value === null || value === "") return "";
    return `<tr><td class="lbl">${escapeHtml(label)}</td><td>${nl2br(value)}${unit ? ` ${escapeHtml(unit)}` : ""}</td></tr>`;
  };

  const blockRow = (label, value) => {
    if (!value) return "";
    return `<tr><td class="lbl">${escapeHtml(label)}</td><td class="block">${nl2br(value)}</td></tr>`;
  };

  const recordHtml = (record, patient, idx) => {
    const vitalsHtml = [
      record.temperature ? `体温 ${record.temperature}℃` : "",
      record.bp_systolic || record.bp_diastolic ? `BP ${record.bp_systolic ?? "-"}/${record.bp_diastolic ?? "-"}` : "",
      record.pulse ? `脈拍 ${record.pulse}` : "",
      record.spo2 ? `SpO2 ${record.spo2}%` : "",
      record.respiration ? `呼吸 ${record.respiration}` : "",
    ].filter(Boolean).join(" ・ ");

    const soapHtml = (record.soap_s || record.soap_o || record.soap_a || record.soap_p)
      ? `
      <section>
        <h2>SOAP</h2>
        <table><tbody>
          ${blockRow("S (主観)", record.soap_s)}
          ${blockRow("O (客観)", record.soap_o)}
          ${blockRow("A (アセスメント)", record.soap_a)}
          ${blockRow("P (計画)", record.soap_p)}
        </tbody></table>
      </section>`
      : "";

    return `
    <article class="page">
      <div class="doc-header">
        <h1>訪問看護記録書 Ⅱ</h1>
        <div class="meta">${escapeHtml(org?.name || "")} ・ 発行日: ${today} ・ ${idx + 1} / ${entries.length}</div>
      </div>

      <section>
        <h2>基本情報</h2>
        <table><tbody>
          ${row("利用者氏名", patient?.name_kanji || "")}
          ${row("記録日", record.record_date)}
          ${row("看護師等氏名", record.nurse_name)}
          ${row("訪問職種", record.visit_profession)}
          ${row("訪問先", record.visit_location)}
          ${row("次回訪問予定日", record.next_visit_date)}
        </tbody></table>
      </section>

      ${vitalsHtml ? `
      <section>
        <h2>バイタルサイン</h2>
        <div class="vitals-line">${escapeHtml(vitalsHtml)}</div>
      </section>` : ""}

      ${(record.daily_living || record.mental_state || record.nursing_actions || record.gaf_score || (record.daily_living_details && Object.values(record.daily_living_details||{}).some(v=>v && String(v).trim()))) ? `
      <section>
        <h2>観察・看護内容</h2>
        <table><tbody>
          ${record.daily_living_details && Object.entries(record.daily_living_details||{}).filter(([,v])=>v && String(v).trim()).length > 0 ? `<tr><td class="lbl">日常生活</td><td>${Object.entries(record.daily_living_details).filter(([,v])=>v && String(v).trim()).map(([k,v]) => `<div><b>${escapeHtml(k)}:</b> ${nl2br(v)}</div>`).join("")}</td></tr>` : ""}
          ${blockRow("日常生活 補足", record.daily_living)}
          ${blockRow("精神状態 / 服薬 / 作業・対人関係", record.mental_state)}
          ${blockRow("実施した看護内容", record.nursing_actions)}
          ${row("GAFスコア", record.gaf_score)}
        </tbody></table>
      </section>` : ""}

      ${soapHtml}

      ${(((record.observation_details && Object.values(record.observation_details || {}).some(v => v && String(v).trim())) || (record.bath_grooming_details && Object.values(record.bath_grooming_details || {}).some(v => v && String(v).trim())) || record.bath_grooming_checks?.length > 0)) ? `
      <section>
        <h2>観察・実施項目</h2>
        <table><tbody>
          ${record.observation_details && Object.entries(record.observation_details || {}).filter(([,v])=>v && String(v).trim()).length > 0 ? `<tr><td class="lbl">観察事項</td><td>${Object.entries(record.observation_details).filter(([,v])=>v && String(v).trim()).map(([k,v]) => `<div><b>${escapeHtml(k)}:</b> ${nl2br(v)}</div>`).join("")}</td></tr>` : ""}
          ${record.bath_grooming_details && Object.entries(record.bath_grooming_details || {}).filter(([,v])=>v && String(v).trim()).length > 0 ? `<tr><td class="lbl">入浴・整容</td><td>${Object.entries(record.bath_grooming_details).filter(([,v])=>v && String(v).trim()).map(([k,v]) => `<div><b>${escapeHtml(k)}:</b> ${nl2br(v)}</div>`).join("")}</td></tr>` : (record.bath_grooming_checks?.length > 0 ? `<tr><td class="lbl">入浴・整容</td><td>${record.bath_grooming_checks.map(c => `<span style="display:inline-block;padding:0.5mm 1.5mm;margin:0.3mm 0.5mm 0.3mm 0;border:0.3pt solid #4A6B8C;border-radius:1mm;font-size:8pt;">☑ ${escapeHtml(c)}</span>`).join("")}</td></tr>` : "")}
        </tbody></table>
      </section>` : ""}

      ${(record.procedures || record.observations || record.summary) ? `
      <section>
        <h2>処置・サマリー</h2>
        <table><tbody>
          ${blockRow("処置内容", record.procedures)}
          ${blockRow("観察事項 補足", record.observations)}
          ${blockRow("サマリー", record.summary)}
        </tbody></table>
      </section>` : ""}

      <div class="signature-area">
        <div>
          <div class="sig-label">記録者署名</div>
          <div class="sig-box">${escapeHtml(record.nurse_name || "")}</div>
        </div>
        <div>
          <div class="sig-label">管理者確認</div>
          <div class="sig-box"></div>
        </div>
      </div>

      <div class="footer">
        <span>${escapeHtml(org?.name || "")}</span>
        <span>TEL: ${escapeHtml(org?.phone || "")} / 管理者: ${escapeHtml(org?.manager_name || "")}</span>
      </div>
    </article>`;
  };

  const summaryHtml = `
    <article class="page summary-page">
      <div class="doc-header">
        <h1>訪問看護記録書 Ⅱ ・ 一括印刷</h1>
        <div class="meta">${escapeHtml(org?.name || "")} ・ 発行日: ${today}</div>
      </div>
      <section>
        <h2>印刷概要</h2>
        <table><tbody>
          <tr><td class="lbl">対象件数</td><td>${entries.length} 件</td></tr>
          ${filterLabel ? `<tr><td class="lbl">フィルタ条件</td><td>${escapeHtml(filterLabel)}</td></tr>` : ""}
          <tr><td class="lbl">出力日時</td><td>${today}</td></tr>
        </tbody></table>
      </section>
      <section>
        <h2>記録一覧</h2>
        <table>
          <thead>
            <tr><th style="width:8mm">#</th><th style="width:28mm">記録日</th><th>利用者</th><th style="width:30mm">看護師等</th><th style="width:24mm">職種</th></tr>
          </thead>
          <tbody>
            ${entries.map((e, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${escapeHtml(e.record.record_date || "")}</td>
                <td>${escapeHtml(e.patient?.name_kanji || "?")}</td>
                <td>${escapeHtml(e.record.nurse_name || "")}</td>
                <td>${escapeHtml(e.record.visit_profession || "")}</td>
              </tr>`).join("")}
          </tbody>
        </table>
      </section>
    </article>`;

  const html = `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="utf-8">
<title>訪問看護記録書Ⅱ 一括印刷 (${entries.length}件)</title>
<style>
  @page { size: A4 portrait; margin: 12mm 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; background: white; color: #111;
    font-family: "Shippori Mincho", "Yu Mincho", "MS Mincho", serif;
    font-size: 10pt; line-height: 1.55;
  }
  article.page {
    page-break-after: always;
    break-after: page;
  }
  article.page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  .doc-header { text-align: center; margin-bottom: 6mm; }
  .doc-header h1 { font-size: 16pt; margin: 0 0 2mm 0; letter-spacing: 0.05em; }
  .doc-header .meta { font-size: 9pt; color: #444; }
  section { margin-bottom: 4mm; page-break-inside: avoid; }
  h2 {
    font-size: 11pt; font-weight: bold; margin: 4mm 0 2mm 0;
    padding-bottom: 1mm; border-bottom: 0.4pt solid #000;
  }
  table { width: 100%; border-collapse: collapse; }
  td, th { padding: 1mm 2mm; vertical-align: top; font-size: 9.5pt; }
  th { background: #f5f5f3; border-bottom: 0.4pt solid #000; text-align: left; }
  td.lbl { font-weight: bold; width: 34mm; background: #fafaf7; }
  td.block { white-space: pre-wrap; }
  tr { page-break-inside: avoid; border-bottom: 0.25pt solid #ddd; }
  .vitals-line { font-size: 10pt; padding: 1.5mm 2mm; border: 0.4pt solid #000; }
  .footer {
    margin-top: 8mm; padding-top: 3mm; border-top: 0.4pt solid #000;
    display: flex; justify-content: space-between; font-size: 8.5pt; color: #555;
  }
  .signature-area {
    margin-top: 10mm; display: flex; gap: 8mm; font-size: 9pt;
  }
  .signature-area .sig-box {
    flex: 1; border-bottom: 0.4pt solid #000; min-height: 14mm; padding: 1mm 2mm;
  }
  .signature-area .sig-label { font-size: 8pt; color: #555; }
  .summary-page h1 { font-size: 18pt; }

  /* ===========================================================
   * 印刷時のみ: 1記録 = A4 1ページに必ず収まるようコンパクト化
   * 画面プレビュー(window内表示)のレイアウトはそのまま維持
   * =========================================================== */
  @media print {
    @page { size: A4 portrait; margin: 10mm 12mm; }
    html, body { font-size: 8.5pt; line-height: 1.35; }

    .doc-header { margin-bottom: 3mm; }
    .doc-header h1 { font-size: 14pt; margin: 0 0 1mm 0; }
    .doc-header .meta { font-size: 8pt; }

    section { margin-bottom: 2mm; }
    h2 { font-size: 9pt; margin: 2mm 0 1mm 0; padding-bottom: 0.5mm; }

    td, th { padding: 0.6mm 1.5mm; font-size: 8pt; }
    td.lbl { width: 28mm; }
    .vitals-line { font-size: 8pt; padding: 0.8mm 1.5mm; }

    .signature-area { margin-top: 4mm; gap: 6mm; font-size: 8pt; }
    .signature-area .sig-box { min-height: 10mm; }
    .signature-area .sig-label { font-size: 7pt; }

    .footer { margin-top: 3mm; padding-top: 1.5mm; font-size: 7.5pt; }

    .summary-page h1 { font-size: 15pt; }
  }
</style>
</head>
<body>
  ${summaryHtml}
  ${entries.map((e, i) => recordHtml(e.record, e.patient, i)).join("\n")}
  <script>
    window.addEventListener("load", function() {
      setTimeout(function() {
        window.focus();
        window.print();
      }, 400);
    });
    window.addEventListener("afterprint", function() {
      window.close();
    });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) {
    alert("ポップアップがブロックされています。ブラウザの設定で許可してください。");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
